/**
 * Breaks the 401 → signinRedirect → Passport → /callback → 401 loop.
 *
 * When the API rejects our bearer with 401 we redirect to Passport once —
 * the token may genuinely be stale. But if Passport still has a live
 * session it bounces us straight back with a fresh code, the exchange
 * succeeds, the API 401s again, and without a guard we'd redirect again:
 * an infinite full-page loop hammering the accounts endpoint (observed at
 * ~80 requests in seconds). In-memory refs can't stop it because every
 * iteration is a fresh page load, so the "already redirected" marker
 * lives in sessionStorage — it survives the reload but stays scoped to
 * the tab.
 *
 * Storage access is wrapped in try/catch: some privacy modes throw on
 * sessionStorage. In that case we degrade to the old once-per-page-load
 * behaviour rather than crash.
 */
const KEY = 'lobby.signinRedirectAt'
const WINDOW_MS = 60_000

/** True when no signin redirect was attempted within the window —
 *  i.e. it's safe to try one (re-)auth round trip. */
export function shouldAttemptSigninRedirect(now = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (raw === null) return true
    const at = Number(raw)
    if (!Number.isFinite(at)) return true
    return now - at >= WINDOW_MS
  } catch {
    return true
  }
}

/** Record that a signin redirect is being attempted right now. */
export function recordSigninRedirect(now = Date.now()): void {
  try {
    window.sessionStorage.setItem(KEY, String(now))
  } catch {
    // Storage unavailable — nothing to record; the guard degrades to
    // once-per-page-load via the in-memory ref in useAccounts.
  }
}

/** Clear the marker after a successful authorized fetch, so a future
 *  legitimate session expiry gets its one redirect again. */
export function clearSigninRedirect(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // Storage unavailable — nothing was recorded either.
  }
}
